import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import { deepFreeze, timestampToDate } from "../util/misc";
import { codesMatch } from "../util/barcode";
import { vendorPOElsewhere } from "../util/vendorOrderNumber";
import { isEmpty, sortBy } from "lodash";
import { diff } from "deep-object-diff";

/**
 * CharacterCode values from Marvin ICIM:
 *   "" (blank) = Assembly (main window unit)
 *   SC = Screen
 *   SH = Sash
 *   GR = Grill
 *   CSG = Casing
 *   BN = Bundle
 *   ST = Storm
 */
export const CHARACTER_CODES = {
  "": "Assembly",
  SC: "Screen",
  SH: "Sash",
  GR: "Grill",
  CSG: "Casing",
  BN: "Bundle",
  ST: "Storm",
};
deepFreeze(CHARACTER_CODES);

// A package. Marvin ships several per line, each with its own quantity, so a
// line's tracking numbers are not interchangeable — receiving one package is
// not receiving the line. The `vendor*` fields are Marvin's own scan record and
// are read-only; the rest is filled in by our receiving and loading flows.
export const TRACKING_LINE_FIELDS = {
  trackingNo: "",
  // The carton the package rides in. Marvin repeats one "E"-prefixed number
  // across every package it consolidates into a box, so a screen/accessory
  // carton whose label prints only this number resolves to several packages
  // at once. Blank on packages Marvin did not consolidate. See findByPackage.
  packageTrackingNo: "",
  trackingQty: 0,
  trackingStatus: "", // Marvin's line status, e.g. "Shipped"
  vendorScanStatus: "", // Marvin's scan, e.g. "SCANNED"
  vendorScanDate: null,
  vendorSignature: "",
  scanStatus: "", // Ours: Loaded, In-Transit, Delivered, Short, Damaged, etc.
  scannedBy: null,
  scannedAt: null,
  receivedBy: null,
  receivedAt: null,
  loadedBy: null,
  loadedAt: null,
};
deepFreeze(TRACKING_LINE_FIELDS);

// Marvin reports back-ordered quantities per line. Placeholder entries with no
// tracking number and a zero quantity are dropped during parsing.
export const BACK_ORDER_FIELDS = {
  trackingNo: "",
  quantity: 0,
  status: "",
};
deepFreeze(BACK_ORDER_FIELDS);

export const SHIPMENT_LINE_FIELDS = {
  // Marvin's order line, as a STRING: accessories carry a suffixed form of
  // their parent's number, so the door is "10" and its handle is "10a".
  vendorLineNumber: "",
  vendorLineBase: 0, // The numeric part, matching SaleItem.line
  isAccessory: false, // True when the line number carried a suffix
  vendorOrderNumber: "",
  // The PO the order was placed under, echoed back by Marvin. Defaults to
  // projectID + saleID, so it resolves the sale this shipment belongs to --
  // see parseVendorPO in functions/vendorShippingParse.js.
  customerPONumber: "",
  orderType: "", // "STANDARD SALES ORDER" / "FIELD SERVICE ORDER"
  jobName: "",
  // The dealer account the order was placed under (one login sees them all).
  customerName: "",
  customerNumber: "",
  saleItemID: null, // Mapped SaleItem ID, set by reconcileShipmentLines
  // A suffixed Marvin line ("10a") is a COMPONENT of its parent unit -- the
  // door's handle -- shipped as a separate package. It resolves to the same
  // sale item as the parent, and must not add to that item's ready quantity:
  // receiving the door package and the handle package is receiving one door.
  isComponent: false,
  // How much evidence backed the match, 0..1. No single signal covers every
  // order -- rough opening is decisive on window orders and absent on parts
  // orders -- so the matcher scores whichever signals both sides supply.
  // See MATCH_CONFIDENCE for the display bands.
  matchConfidence: 0,
  // Which signals agreed, e.g. ["roughOpening", "room", "quantity"].
  matchSignals: [],
  // False when a rival scored the same and was a DIFFERENT product. Three
  // identical Office double-hungs are interchangeable and this stays true;
  // a balance against a pivot pin is a guess worth a human look.
  matchInterchangeable: true,

  description: "", // Marvin's ShortDescription
  productType: "",
  unitType: "",
  characterCode: "", // See CHARACTER_CODES
  markUnit: "", // Marvin's opening label, e.g. "Rear Hallway"
  qtyOrdered: 0,
  qtyShipped: 0, // Less than qtyOrdered on a partially shipped line
  listPrice: 0,
  netPrice: 0,
  weekOfDelivery: "",
  lineStatus: "",
  orderStatus: "", // e.g. "PARTIALLY SHIPPED"

  // Marvin's product spec lines, in sequence. Accessories carry none.
  // ShortDescription ("CUIFD G2 6 9/16 X ASSY") and a sale item's description
  // ("Clad Inswing French Door G2 - Right Hand") use different vocabularies and
  // cannot be compared. These notes are the bridge: they hold the same spec the
  // descriptions were imported from, so the fields below are directly
  // comparable to what a sale item shows.
  notes: [],
  callNumber: "", // Sale item description shows this as "Call Number 3080"
  roughOpening: null, // { width, height } in decimal inches, e.g. 38.4375 x 98.5
  configuration: "", // "X", "XX", "O"
  handing: "", // "Left Hand" / "Right Hand"

  trackingLines: [], // Array of TRACKING_LINE_FIELDS
  backOrders: [], // Array of BACK_ORDER_FIELDS
};
deepFreeze(SHIPMENT_LINE_FIELDS);

export const VENDOR_SHIPMENT_FIELDS = {
  vendorOrderNumber: "",
  truckNumber: "",
  stopID: "",
  loadName: "",
  stopName: "",
  shipFrom: "", // Originating plant
  truckStatus: "",
  truckType: "",
  carrierCode: "",
  reportType: "", // DETAIL or SUMMARY
  shippingDate: null,
  etaDate: null,
  plannedDepartureDate: null,
  destination: null, // { name, address, city, state, zip }
  lines: [], // Array of SHIPMENT_LINE_FIELDS

  // Denormalized by functions/vendorShippingSync.js (writeShipments) and
  // stampTrackingLine so the receiving listeners can filter server-side:
  // `outstanding` is the not-yet-received package count "pending" means, and
  // vendor/orderPO spare the page a fetch per order.
  outstanding: 0,
  vendor: "",
  orderPO: "",
  // The POs on the order's lines that name no sale, once the sync has shown
  // the order is this sale's -- Marvin's PO naming it on another line, the
  // order's own PO, the sale's quote number, the office's "This order is
  // ours" (verifiedPOsOf in functions/vendorShippingSync.js) -- else []. The
  // dock receives such a package without asking (vendorPOElsewhere).
  verifiedVendorPOs: [],
  // Mirrors sale.status.orders.<orderID>.fullyReceived (writeShipments and
  // touchSale keep it): an order received by packing slip or by hand expects
  // nothing more from this truck, whatever the scan stamps say.
  orderReceived: false,

  // Sync metadata
  lastSynced: null,
  syncedBy: null,

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(VENDOR_SHIPMENT_FIELDS);

// Display bands for matchConfidence. Thresholds mirror
// functions/vendorShipmentMatch.js CONFIDENCE.
export const MATCH_CONFIDENCE = {
  unknown: {
    min: 0,
    label: "Match confidence not recorded",
    icon: "help",
    color: "grey",
  },
  high: {
    min: 0.75,
    label: "Confident match",
    icon: "success",
    color: "positive",
  },
  medium: {
    min: 0.5,
    label: "Probable match",
    icon: "warning",
    color: "warning",
  },
  low: { min: 0, label: "Weak match", icon: "warning", color: "negative" },
};
deepFreeze(MATCH_CONFIDENCE);

export const TRUCK_STATUS_TYPES = {
  scheduled: {
    label: "Scheduled",
    icon: "pending",
    color: "info",
  },
  loaded: {
    label: "Loaded",
    icon: "loaded",
    color: "warning",
  },
  "in-transit": {
    label: "In transit",
    icon: "vehicle",
    color: "warning",
  },
  delivered: {
    label: "Delivered",
    icon: "delivered",
    color: "positive",
  },
};
deepFreeze(TRUCK_STATUS_TYPES);

// Firestore cannot address an array element by index in a field-path update.
// `updateDoc(ref, {"lines.0.trackingLines.1.receivedAt": v})` does not touch
// slot 0: the server reads "0" as a MAP key, and since an array is not a map
// it replaces `lines` with a map holding only the updated path --
//   { "0": { trackingLines: { "1": { receivedAt: v } } } }
// Every other line and package on the truck, and even the stamped package's
// own tracking number, are gone from the document (verified on the Firestore
// emulator, 2026-09-16). Once that has happened every `.forEach`/`.reduce`
// over `lines` throws -- "(this.data.lines || []).forEach is not a function"
// -- and the receiving page dies on load.
//
// These helpers turn such a map back into an array so a corrupted document can
// never crash a getter. They can NOT recover what the write dropped: the only
// repair is a fresh copy from the vendor (RESYNC_VENDOR_SHIPMENTS ->
// syncVendorShipping, or the poller), which rewrites `lines` from the feed.
// A shipment built from such a document says so via `linesCorrupted`.
function toArray(value) {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") {
    return Object.keys(value)
      .sort((a, b) => Number(a) - Number(b))
      .map((key) => value[key]);
  }
  return [];
}

/**
 * Packages still to receive: tracking lines with a real tracking number and
 * no receivedAt. Mirrors functions/vendorShipmentStamps.js countOutstanding
 * (functions and src share no code -- same convention as MATCH_CONFIDENCE):
 * stampTrackingLine recomputes the stored `outstanding` with it on every
 * scan stamp, and it is what the outstanding>0 listener filter counts.
 */
export function countOutstanding(lines) {
  if (!Array.isArray(lines)) return 0;
  let count = 0;
  for (const line of lines) {
    const trackingLines =
      line && Array.isArray(line.trackingLines) ? line.trackingLines : [];
    for (const trackingLine of trackingLines) {
      if (!trackingLine) continue;
      if (!String(trackingLine.trackingNo || "").trim()) continue;
      if (trackingLine.receivedAt) continue;
      count++;
    }
  }
  return count;
}

/**
 * The `outstanding` a shipment document stores: nothing once its order is
 * received some other way. Mirrors functions/vendorShipmentStamps.js
 * shipmentOutstanding.
 */
export function shipmentOutstanding(data) {
  if (!data || data.orderReceived) return 0;
  return countOutstanding(data.lines);
}

/**
 * The first package on a shipment's lines with this tracking number, or null.
 * For plain document data (stampTrackingLine reads inside a transaction);
 * an instance has findByBarcode, which also reads a label the scanner
 * suffixed or a piece mark (codesMatch). A scan records the package's own
 * tracking number, so an exact match is the right one here.
 */
export function findTrackingLine(lines, trackingNo) {
  const wanted = String(trackingNo || "").trim();
  if (!wanted || !Array.isArray(lines)) return null;
  for (const line of lines) {
    const trackingLines =
      line && Array.isArray(line.trackingLines) ? line.trackingLines : [];
    for (const trackingLine of trackingLines) {
      if (trackingLine && String(trackingLine.trackingNo).trim() === wanted) {
        return trackingLine;
      }
    }
  }
  return null;
}

export function normalizeShipmentLines(lines) {
  return toArray(lines).map((line) => {
    if (!line || typeof line !== "object") return line;
    const fixed = { ...line };
    if (fixed.trackingLines != null)
      fixed.trackingLines = toArray(fixed.trackingLines);
    if (fixed.backOrders != null) fixed.backOrders = toArray(fixed.backOrders);
    return fixed;
  });
}

// True when `lines`, or any line's trackingLines/backOrders, is a map rather
// than an array -- i.e. the document on disk is a remnant of the corruption
// above and is worth asking the vendor to replace.
export function shipmentLinesCorrupted(lines) {
  if (lines != null && !Array.isArray(lines)) return true;
  return (Array.isArray(lines) ? lines : []).some(
    (line) =>
      line &&
      typeof line === "object" &&
      ((line.trackingLines != null && !Array.isArray(line.trackingLines)) ||
        (line.backOrders != null && !Array.isArray(line.backOrders)))
  );
}

export default class VendorShipment extends DataStructure {
  constructor(projectID, saleID, orderID, id, data = {}) {
    data = sanitize(data, VENDOR_SHIPMENT_FIELDS);

    // Convert to Date
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);
    data.lastSynced = timestampToDate(data.lastSynced);
    data.shippingDate = timestampToDate(data.shippingDate);
    data.etaDate = timestampToDate(data.etaDate);
    data.plannedDepartureDate = timestampToDate(data.plannedDepartureDate);

    // A document the array-index bug truncated (see normalizeShipmentLines):
    // remembered so a page can ask the vendor for a fresh copy, and coerced so
    // every getter below sees `lines` as an array whatever the disk holds.
    const linesCorrupted = shipmentLinesCorrupted(data.lines);
    data.lines = normalizeShipmentLines(data.lines);

    super(data);

    this.id = id || null;
    this.projectID = projectID || null;
    this.saleID = saleID || null;
    this.orderID = orderID || null;
    this.linesCorrupted = linesCorrupted;
    this.permissions = Object.freeze({
      edit: this.canBeEditedBy,
    });
  }

  // Permissions

  canBeEditedBy(user) {
    return user.isEmployee;
  }

  // Database

  get path() {
    return VendorShipment.getPath(
      this.projectID,
      this.saleID,
      this.orderID,
      this.id
    );
  }

  static getPath(projectID, saleID, orderID, id) {
    let path = `projects/${projectID}/sales/${saleID}/orders/${orderID}/vendorShipments`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(shipment) {
    return shipment.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new VendorShipment(
      snapshot.ref.parent.parent.parent.parent.parent.parent.id,
      snapshot.ref.parent.parent.parent.parent.id,
      snapshot.ref.parent.parent.id,
      snapshot.id,
      data
    );
  }

  // Computed

  get sale() {
    return this.saleID
      ? store.state.main.sales[this.projectID + this.saleID]
      : null;
  }

  get order() {
    return this.orderID ? store.state.main.orders[this.orderID] : null;
  }

  get allTrackingNumbers() {
    const numbers = [];
    (this.data.lines || []).forEach((line) => {
      (line.trackingLines || []).forEach((tl) => {
        if (tl.trackingNo) {
          numbers.push(tl.trackingNo);
        }
      });
    });
    return numbers;
  }

  get totalQtyOrdered() {
    return (this.data.lines || []).reduce(
      (sum, line) => sum + (line.qtyOrdered || 0),
      0
    );
  }

  get totalQtyShipped() {
    return (this.data.lines || []).reduce(
      (sum, line) => sum + (line.qtyShipped || 0),
      0
    );
  }

  get totalTracked() {
    return this.allTrackingNumbers.length;
  }

  get totalReceived() {
    let count = 0;
    (this.data.lines || []).forEach((line) => {
      (line.trackingLines || []).forEach((tl) => {
        if (tl.receivedAt) {
          count++;
        }
      });
    });
    return count;
  }

  // When this truck should reach the warehouse. Marvin reports an ETA per
  // truck; the ship date stands in for it when the ETA is blank, and the
  // planned departure is the last resort. Null for the placeholder records
  // Marvin returns before a truck is scheduled.
  get expectedDate() {
    return (
      this.data.etaDate ||
      this.data.shippingDate ||
      this.data.plannedDepartureDate ||
      null
    );
  }

  get totalLoaded() {
    let count = 0;
    (this.data.lines || []).forEach((line) => {
      (line.trackingLines || []).forEach((tl) => {
        if (tl.loadedAt) {
          count++;
        }
      });
    });
    return count;
  }

  get assemblyLines() {
    return (this.data.lines || []).filter(
      (l) => !l.characterCode || l.characterCode === ""
    );
  }

  get subLines() {
    return (this.data.lines || []).filter(
      (l) => l.characterCode && l.characterCode !== ""
    );
  }

  /**
   * Find a tracking line by barcode (tracking number).
   * Vendor barcodes may use Code 3 of 9; the left 6 characters may be truncated.
   */
  findByBarcode(barcode) {
    // The scanner's suffix ("VDD629." for "VDD629") is stripped on both
    // sides, so a stored tracking number matches however it was read.
    for (const line of this.data.lines || []) {
      for (const tl of line.trackingLines || []) {
        if (codesMatch(barcode, tl.trackingNo)) {
          return { line, trackingLine: tl };
        }
      }
    }
    return null;
  }

  /**
   * Every package in the carton a scanned box barcode names.
   *
   * Marvin ships screens, hardware and accessories in a consolidated box and
   * prints only the carton's number (packageTrackingNo) on it -- the packages
   * inside carry their own tracking numbers, but those are never on the box.
   * One scan of that number therefore has to receive every package sharing
   * it, which findByBarcode (one tracking number, one package) cannot express.
   *
   * Returns the matching packages with their positions, so each can be stamped
   * against the vendor's line; empty when the barcode names no carton. A box
   * barcode is only ever printed on consolidated cartons, so a blank
   * packageTrackingNo never matches (codesMatch already refuses empty).
   */
  findByPackage(barcode) {
    const found = [];
    (this.data.lines || []).forEach((line, lineIndex) => {
      (line.trackingLines || []).forEach((tl, trackingIndex) => {
        if (codesMatch(barcode, tl.packageTrackingNo)) {
          found.push({ line, trackingLine: tl, lineIndex, trackingIndex });
        }
      });
    });
    return found;
  }

  /**
   * Marvin's PO for one of this shipment's lines when it does not name the
   * order the shipment is stored under, else "" (vendorPOElsewhere in
   * src/util/vendorOrderNumber.js). The dock asks before receiving such a
   * package: it may be another job's order, stored here because an ack
   * carries its number.
   */
  vendorPOElsewhere(line) {
    return vendorPOElsewhere(line && line.customerPONumber, {
      projectID: this.projectID,
      saleID: this.saleID,
      orderPO: this.data.orderPO,
      verifiedPOs: this.data.verifiedVendorPOs,
    });
  }

  /**
   * Get a character code label.
   */
  /**
   * Band a line's match confidence for display, or null when unmatched.
   */
  static getMatchConfidence(line) {
    if (!line || !line.saleItemID) return null;
    const score = line.matchConfidence || 0;
    // Shipments synced before scoring existed carry a saleItemID and no score.
    // Banding that as "weak" would paint every historic line red for something
    // it was never measured on -- say unknown until the next sync scores it.
    if (!score)
      return { key: "unknown", score: 0, ...MATCH_CONFIDENCE.unknown };
    const key =
      score >= MATCH_CONFIDENCE.high.min
        ? "high"
        : score >= MATCH_CONFIDENCE.medium.min
        ? "medium"
        : "low";
    return { key, score, ...MATCH_CONFIDENCE[key] };
  }

  static getCharacterCodeLabel(code) {
    return CHARACTER_CODES[code] || code || "Assembly";
  }

  isEqual(shipment) {
    return this.id === shipment.id && isEmpty(diff(this.data, shipment.data));
  }
}
